import { useLayoutEffect, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLenis } from "lenis/react";

/**
 * Scroll reset on route change — defensive version.
 *
 * Root cause we are protecting against:
 *   1. Lenis (smooth-scroll library) keeps its own internal `.scroll` value
 *      and translates the page via CSS `transform`. Native `scrollIntoView`
 *      or `window.scrollTo` set `documentElement.scrollTop` directly, without
 *      telling Lenis — Lenis then re-applies its own transform on the next
 *      animation frame and effectively "un-scrolls" the page. This paints as
 *      a completely BLACK page whenever the new route content is shorter
 *      than the previous scroll offset (classic symptom after visiting
 *      `nextech.law/#assessment`).
 *
 *   2. On initial cold load with `#assessment` in the URL, the browser tries
 *      to jump to the anchor before React has mounted the section.
 *
 * Strategy:
 *   - `useLayoutEffect` fires SYNCHRONOUSLY after DOM mutation but BEFORE
 *     the browser paints — this is the only place where we can move the
 *     scroll before the user sees anything.
 *   - Reset BOTH native scroll AND Lenis' internal state to 0 first.
 *   - For hash-anchor navigation, wait ONE animation frame so React finished
 *     committing the new route's DOM, THEN scroll Lenis to the anchor.
 *   - Re-fire when `lenis` becomes available (context-based), so the very
 *     first mount also benefits from Lenis sync.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const lenis = useLenis();

  useLayoutEffect(() => {
    // Always reset native scroll to zero first — this guarantees the browser
    // starts painting the new route at the top even if Lenis is still
    // initialising or somehow desynced.
    if (!hash) {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      if (lenis) {
        // Cancel any in-flight smooth scroll so it doesn't fight us.
        try { lenis.stop(); } catch (_) {}
        // Recalculate scroll extent based on the (just committed) new DOM.
        try { lenis.resize(); } catch (_) {}
        // Snap Lenis' internal position to 0 without animation.
        try { lenis.scrollTo(0, { immediate: true, force: true, lock: false }); } catch (_) {}
        // Resume normal wheel handling.
        try { lenis.start(); } catch (_) {}
      }
      return;
    }

    // Hash present. Reset first, then scroll to the anchor next frame so the
    // element has real layout coordinates.
    window.scrollTo(0, 0);
    if (lenis) {
      try { lenis.stop(); } catch (_) {}
      try { lenis.scrollTo(0, { immediate: true, force: true }); } catch (_) {}
    }

    // The anchor target may not exist yet when the destination route is
    // lazy-loaded (e.g. /services) or when a client-side redirect added the
    // hash AFTER initial load (e.g. /cra-control-room → /services#…), so the
    // browser never performs its native fragment scroll. Retry per animation
    // frame (bounded ~2s) until the element is committed, then scroll once.
    let cancelled = false;
    let attempts = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(hash.replace(/^#/, ""));
      if (!el) {
        if (attempts++ < 120) {
          requestAnimationFrame(tryScroll);
        } else if (lenis) {
          try { lenis.start(); } catch (_) {}
        }
        return;
      }
      if (lenis) {
        try { lenis.resize(); } catch (_) {}
        try { lenis.scrollTo(el, { immediate: true, force: true }); } catch (_) {}
        try { lenis.start(); } catch (_) {}
      } else {
        el.scrollIntoView({ block: "start" });
      }
    };
    const raf = requestAnimationFrame(tryScroll);

    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [pathname, hash, lenis]);

  // Defensive resync: whenever the window is resized OR an iframe / async
  // content changes the document height, ensure Lenis knows about it.
  useEffect(() => {
    if (!lenis) return;
    const onResize = () => { try { lenis.resize(); } catch (_) {} };
    window.addEventListener("resize", onResize);
    // Also observe body-size changes (image loads, iframe growth, etc.)
    let ro;
    try {
      ro = new ResizeObserver(onResize);
      ro.observe(document.body);
    } catch (_) {}
    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [lenis]);

  return null;
}
